import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.dataset.productUrl || this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }



  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    const jsonEl = this.querySelector('.product-variants-json');
    if (jsonEl) {
      try {
        this.variantsData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('Error parsing product variants JSON', e);
      }
    }

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#syncProductTitle(event.detail.resource);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);



    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();

    this.#syncWishlistButton(event.detail.resource);
  };

  /**
   * Syncs the wishlist button to the active variant.
    * @param {any} variant
   */
  #syncWishlistButton(variant) {
    const wishlistBtn = this.querySelector('[data-wishlist-button]');
    if (!(wishlistBtn instanceof HTMLButtonElement)) return;
    if (!variant) return;

    if (this.variantsData) {
      const found = this.variantsData.find(v => String(v.id) === String(variant.id));
      if (found) {
        variant = found;
      }
    }

    const featuredMedia =
      variant.featured_media_src ||
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      variant.featured_image ||
      '';

    wishlistBtn.dataset.productId = this.dataset.productId || wishlistBtn.dataset.productId || '';
    if (this.productPageUrl) {
      const url = new URL(this.productPageUrl, window.location.origin);
      wishlistBtn.dataset.productHandle = url.pathname.split('/').filter(Boolean).pop() || wishlistBtn.dataset.productHandle || '';
    }

    wishlistBtn.dataset.variantId = String(variant.id || '');
    wishlistBtn.dataset.variantTitle = variant.title || '';
    wishlistBtn.dataset.variantImage = featuredMedia || '';
    wishlistBtn.dataset.variantPrice = String(variant.price ?? '');
    wishlistBtn.dataset.variantCompareAtPrice = String(variant.compare_at_price ?? 0);
    wishlistBtn.dataset.variantOptions = JSON.stringify(variant.options || []);
    wishlistBtn.dataset.displayTitle = (variant.title && variant.title !== 'Default Title') ? variant.title : (wishlistBtn.dataset.productTitle || '');

    delete wishlistBtn.dataset.amolihPrice;
    delete wishlistBtn.dataset.amolihCurrency;

    window.AmolihWishlist?.syncButton?.(wishlistBtn);
  }

  /**
   * Syncs any visible product title element to the active variant title.
   * @param {any} variant
   */
  #syncProductTitle(variant) {
    const titleElements = this.querySelectorAll(
      '[data-product-title]:not([data-wishlist-button]), .product-card__title, .view-product-title, product-title .title-text'
    );

    for (const titleElement of titleElements) {
      if (titleElement instanceof HTMLElement) {
        const defaultTitle = titleElement.dataset.productDefaultTitle || '';
        if (defaultTitle) {
          titleElement.textContent = defaultTitle;
        }
      }
    }
  }

  /**
   * Returns the variant input that matches a slide.
   * @param {HTMLElement} slide
   * @returns {HTMLInputElement | null}
   */
  #getVariantInputFromSlide(slide) {
    const picker = this.variantPicker;
    if (!picker) return null;

    const slideVariantId = slide.dataset.variantId;
    if (slideVariantId) {
      const byVariantId = picker.querySelector(`input[data-variant-id="${slideVariantId}"]`);
      if (byVariantId instanceof HTMLInputElement) return byVariantId;
    }

    const slideId = slide.getAttribute('slide-id');
    if (!slideId) return null;

    const byMediaId = picker.querySelector(`input[data-option-media-id="${slideId}"]`);
    return byMediaId instanceof HTMLInputElement ? byMediaId : null;
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainers.length > 0) {
      for (const priceContainer of priceContainers) {
        morph(priceContainer, newPriceElement.cloneNode(true));
      }
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink instanceof HTMLAnchorElement) {
        if (productCardLink.dataset.productUrl != null) {
          productCardLink.dataset.productUrl = productUrl;
        } else {
          productCardLink.href = productUrl;
        }
      }
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        if (cardGalleryLink.dataset.productUrl != null) {
          cardGalleryLink.dataset.productUrl = productUrl;
        } else {
          cardGalleryLink.href = productUrl;
        }
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        if (productTitleLink.dataset.productUrl != null) {
          productTitleLink.dataset.productUrl = productUrl;
        } else {
          productTitleLink.href = productUrl;
        }
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    const slide = event.detail.slide;
    if (!(slide instanceof HTMLElement)) return;

    const img = slide.querySelector('img');
    const imgSrc = img?.currentSrc || img?.src;
    if (imgSrc && this.closest('product-card-link')) {
      this.closest('product-card-link').setAttribute('data-featured-media-url', imgSrc);
    }

    const slideProductUrl = slide.dataset.productUrl;
    if (!slideProductUrl) return;

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      if (productCardLink.dataset.productUrl != null) {
        productCardLink.dataset.productUrl = slideProductUrl;
      } else {
        productCardLink.href = slideProductUrl;
      }
    }

    if (productTitleLink instanceof HTMLAnchorElement) {
      if (productTitleLink.dataset.productUrl != null) {
        productTitleLink.dataset.productUrl = slideProductUrl;
      } else {
        productTitleLink.href = slideProductUrl;
      }
    }

    if (cardGalleryLink instanceof HTMLAnchorElement) {
      if (cardGalleryLink.dataset.productUrl != null) {
        cardGalleryLink.dataset.productUrl = slideProductUrl;
      } else {
        cardGalleryLink.href = slideProductUrl;
      }
    }

    if (!event.detail.userInitiated) return;
    if (slide.getAttribute('variant-image') == null) return;

    if (slide.dataset.variantId && this.variantsData) {
      const variant = this.variantsData.find(v => String(v.id) === String(slide.dataset.variantId));
      if (variant) {
        this.updatePriceDisplay(variant);
        this.#syncWishlistButton(variant);
      }
    }

    const variantInput = this.#getVariantInputFromSlide(slide);
    if (variantInput instanceof HTMLInputElement && !variantInput.checked) {
      variantInput.checked = true;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Updates the price display client-side when sliding or variant changes.
   * @param {object} variant
   */
  updatePriceDisplay(variant) {
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    if (priceContainers.length === 0) return;

    const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';

    const formatMoney = (amount, currency, isMinorUnits = false) => {
      const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
      if (isNaN(num)) return '';
      let finalAmount = isMinorUnits ? (num / 100) : num;
      if (currency === 'INR') {
        finalAmount = Math.round(finalAmount);
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(finalAmount);
      } else {
        const locale = { USD: 'en-US', GBP: 'en-GB', CAD: 'en-CA' }[currency] || 'en-GB';
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency || 'GBP',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(finalAmount);
      }
    };

    for (const priceContainer of priceContainers) {
      priceContainer.removeAttribute('data-amolih-price');
      priceContainer.removeAttribute('data-amolih-currency');
      priceContainer.removeAttribute('data-amolih-done');

      const formattedPrice = formatMoney(variant.price, activeCurrency, true);
      const priceRange = priceContainer.querySelector('.price-range');
      if (priceRange) {
        const priceEl = document.createElement('span');
        priceEl.className = 'price';
        priceEl.textContent = formattedPrice;
        priceRange.replaceWith(priceEl);
      } else {
        const priceEl = priceContainer.querySelector('.price');
        if (priceEl) priceEl.textContent = formattedPrice;
      }

      const compareEl = priceContainer.querySelector('.compare-at-price');
      if (compareEl) {
        const group = compareEl.closest('[role="group"]');
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price, activeCurrency, true);
          if (group) group.style.display = '';
          else compareEl.style.display = '';
        } else {
          if (group) group.style.display = 'none';
          else compareEl.style.display = 'none';
        }
      }
    }
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    const productUrl = this.productPageUrl;
    if (!productUrl) return;
    const linkURL = new URL(productUrl, window.location.origin);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    if (targetLink && targetLink.dataset.productUrl) {
      event.preventDefault();
      const targetUrl = new URL(targetLink.dataset.productUrl, window.location.origin);
      this.#navigateToURL(event, targetUrl);
      return;
    }

    // Let the native navigation handle the click if it was on a link, unless the card uses a data-only URL.
    if (!targetLink || this.dataset.disableHoverPrefetch === 'true') {
      if (targetLink) event.preventDefault();
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.productPageUrl, window.location.origin);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      const newUrl = currentUrl.toString();
      const { productCardLink, productTitleLink, cardGalleryLink } = this.parentProductCard.refs;
      if (productCardLink instanceof HTMLAnchorElement) {
        if (productCardLink.dataset.productUrl != null) productCardLink.dataset.productUrl = newUrl;
        productCardLink.href = newUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        if (productTitleLink.dataset.productUrl != null) productTitleLink.dataset.productUrl = newUrl;
        productTitleLink.href = newUrl;
      }
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        if (cardGalleryLink.dataset.productUrl != null) cardGalleryLink.dataset.productUrl = newUrl;
        cardGalleryLink.href = newUrl;
      }
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
